import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import * as m365Api from "@shared-core/data/api/m365";
import { useSearchParams } from "react-router";
import type * as API from "@shared-core/data/api-types";
import { useAuth, useIsAdmin } from "../app/auth/hooks";
import {
  Settings,
  Mail,
  CalendarDays,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Unplug,
} from "lucide-react";
import { Button } from "../app/components/primitives/button";
import { Badge } from "../app/components/primitives/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../app/components/primitives/alert-dialog";
import { formatDistanceToNow } from "date-fns";

type ConnectionState = "loading" | "not_connected" | "connected" | "error";

export function SettingsIntegrationsPage() {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const [connections, setConnections] = useState<
    API.ConnectionHealthResponse[]
  >([]);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("loading");
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectingUserId, setDisconnectingUserId] = useState<string | null>(
    null,
  );
  const [flashMessage, setFlashMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Check for OAuth callback result in query params
  useEffect(() => {
    const m365 = searchParams.get("m365");
    if (m365 === "connected") {
      setFlashMessage({
        type: "success",
        text: "Microsoft 365 connected successfully. Your email and calendar sync will start shortly.",
      });
    } else if (m365 === "invalid_state") {
      setFlashMessage({
        type: "error",
        text: "Connection failed: invalid or expired OAuth state. Please try again.",
      });
    } else if (m365 === "tenant_not_allowed") {
      setFlashMessage({
        type: "error",
        text: "Connection failed: your Microsoft tenant is not in the allowed list. Contact your administrator.",
      });
    } else if (m365 === "token_exchange_failed") {
      setFlashMessage({
        type: "error",
        text: "Connection failed: could not exchange the authorization code. Please try again.",
      });
    }
  }, [searchParams]);

  // Load connection health
  useEffect(() => {
    setConnectionState("loading");
    dataAdapter
      .getM365ConnectionHealth()
      .then((resp) => {
        setConnections(resp);
        setConnectionState(
          resp.some((c) => c.app_user_id === user?.id)
            ? "connected"
            : "not_connected",
        );
      })
      .catch(() => {
        setConnectionState("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; adapter identity is stable in-app
  }, []);

  // The current user's own connection — never assume position in the array,
  // since admins receive every user's connection from this endpoint.
  const myConnection = connections.find((c) => c.app_user_id === user?.id);
  // Admin-only: everyone else's connections.
  const otherConnections = isAdmin
    ? connections.filter((c) => c.app_user_id !== user?.id)
    : [];
  // One list inside the M365 card rather than a second card below it: own
  // connection first, then everyone else's. A non-admin only ever sees
  // their own row.
  const visibleConnections = myConnection
    ? [myConnection, ...otherConnections]
    : otherConnections;

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const data = await m365Api.startOAuthFlow();
      capabilities.redirectTo(data.consent_url);
    } catch {
      setFlashMessage({
        type: "error",
        text: "Failed to start the connection process. Please try again.",
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await m365Api.disconnect();
      setConnections((prev) => prev.filter((c) => c.app_user_id !== user?.id));
      setConnectionState("not_connected");
      setFlashMessage({ type: "success", text: "Microsoft 365 disconnected." });
    } catch {
      setFlashMessage({
        type: "error",
        text: "Failed to disconnect. Please try again.",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleDisconnectOther = async (appUserId: string) => {
    setDisconnectingUserId(appUserId);
    try {
      await m365Api.disconnect(appUserId);
      setConnections((prev) => prev.filter((c) => c.app_user_id !== appUserId));
      setFlashMessage({
        type: "success",
        text: "Microsoft 365 connection disconnected.",
      });
    } catch {
      setFlashMessage({
        type: "error",
        text: "Failed to disconnect that account. Please try again.",
      });
    } finally {
      setDisconnectingUserId(null);
    }
  };

  const healthStatusDisplay = (status: string) => {
    switch (status) {
      case "healthy":
        return {
          label: "Connected",
          variant: "default" as const,
          icon: CheckCircle2,
          color: "text-green-600",
        };
      case "backfilling":
        return {
          label: "Syncing history...",
          variant: "secondary" as const,
          icon: RefreshCw,
          color: "text-blue-600",
        };
      case "stale":
        return {
          label: "Stale",
          variant: "secondary" as const,
          icon: AlertTriangle,
          color: "text-yellow-600",
        };
      case "broken_needs_reconnect":
        return {
          label: "Needs reconnect",
          variant: "destructive" as const,
          icon: XCircle,
          color: "text-red-600",
        };
      case "disconnected":
        return {
          label: "Disconnected",
          variant: "outline" as const,
          icon: Unplug,
          color: "text-muted-foreground",
        };
      default:
        return {
          label: status,
          variant: "outline" as const,
          icon: Settings,
          color: "text-muted-foreground",
        };
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your integrations and account connections.
        </p>
      </div>

      {/* Flash message from OAuth callback */}
      {flashMessage && (
        <div
          className={`p-4 rounded-lg border flex items-start gap-3 ${
            flashMessage.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flashMessage.type === "success" ? (
            <CheckCircle2 className="size-5 shrink-0 mt-0.5" />
          ) : (
            <XCircle className="size-5 shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium">{flashMessage.text}</p>
          </div>
          <button
            onClick={() => setFlashMessage(null)}
            className="text-sm opacity-60 hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}

      {/* Microsoft 365 Integration Card */}
      <div className="border border-border rounded-lg bg-card">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Mail className="size-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-medium">Microsoft 365</h2>
              <p className="text-sm text-muted-foreground">
                Connect your Outlook email and calendar to capture customer
                communications automatically.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {connectionState === "loading" && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-sm">Checking connection status...</span>
            </div>
          )}

          {connectionState === "error" && (
            <div className="text-sm text-muted-foreground">
              Could not load connection status. Please refresh the page.
            </div>
          )}

          {/* Connect CTA — only when the signed-in user has no connection of
              their own. Admins may still see other people's in the list. */}
          {connectionState === "not_connected" && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground space-y-2">
                <p>
                  Connecting your Microsoft 365 account will allow the system
                  to:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li className="flex items-start gap-2">
                    <Mail className="size-4 shrink-0 mt-0.5 text-blue-500" />
                    <span>
                      Capture emails to and from your customers automatically
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CalendarDays className="size-4 shrink-0 mt-0.5 text-violet-500" />
                    <span>Sync calendar meetings with customer contacts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 shrink-0 mt-0.5 text-green-500" />
                    <span>Push tickets to your Outlook calendar as events</span>
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground mt-3">
                  Emails involving a known customer contact are stored with
                  their subject and a short preview. Other emails in your
                  mailbox are recorded by subject line only, with no content
                  kept — and if they're marked Private, Personal, or
                  Confidential in Outlook, they're skipped entirely and never
                  stored.
                </p>
              </div>

              <Button
                onClick={handleConnect}
                disabled={connecting}
                className="gap-2"
              >
                {connecting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ExternalLink className="size-4" />
                )}
                {connecting
                  ? "Redirecting to Microsoft..."
                  : "Connect Microsoft 365"}
              </Button>
            </div>
          )}

          {/* Every connection the viewer may see, as one list: their own
              first, then everyone else's (admins only). */}
          {visibleConnections.length > 0 && (
            <div className="divide-y divide-border rounded-lg border border-border">
              {visibleConnections.map((connection) => {
                const display = healthStatusDisplay(connection.health_status);
                const Icon = display.icon;
                const isSelf = connection.app_user_id === user?.id;
                const isDisconnectingThis = isSelf
                  ? disconnecting
                  : disconnectingUserId === connection.app_user_id;
                return (
                  <div
                    key={connection.id}
                    className="p-4 flex flex-wrap items-start justify-between gap-3"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <Icon
                        className={`size-5 shrink-0 mt-0.5 ${display.color}`}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium break-all">
                            {connection.m365_user_principal_name}
                          </span>
                          {isSelf && <Badge variant="outline">You</Badge>}
                          <Badge variant={display.variant}>
                            {display.label}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Connected{" "}
                          {formatDistanceToNow(
                            new Date(connection.connected_at),
                            { addSuffix: true },
                          )}
                          {" · Last sync "}
                          {connection.last_successful_sync_at
                            ? formatDistanceToNow(
                                new Date(connection.last_successful_sync_at),
                                { addSuffix: true },
                              )
                            : "never"}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 break-all">
                          {connection.granted_scopes.join(", ") || "No scopes"}
                        </div>
                        {connection.health_status ===
                          "broken_needs_reconnect" && (
                          <p className="text-xs text-red-700 mt-1">
                            {connection.health_status_reason ||
                              "The connection has stopped working and needs reconnecting."}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {isSelf &&
                        connection.health_status ===
                          "broken_needs_reconnect" && (
                          <Button
                            onClick={handleConnect}
                            disabled={connecting}
                            size="sm"
                            className="gap-2"
                          >
                            {connecting ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <RefreshCw className="size-4" />
                            )}
                            Reconnect
                          </Button>
                        )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isDisconnectingThis}
                            className="gap-2 text-destructive hover:text-destructive"
                          >
                            {isDisconnectingThis ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Unplug className="size-4" />
                            )}
                            Disconnect
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {isSelf
                                ? "Disconnect Microsoft 365?"
                                : `Disconnect ${connection.m365_user_principal_name}?`}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {isSelf
                                ? "Your email and calendar sync will stop. You can reconnect at any time."
                                : "Their email and calendar sync will stop. They can reconnect at any time."}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                isSelf
                                  ? handleDisconnect()
                                  : handleDisconnectOther(
                                      connection.app_user_id,
                                    )
                              }
                            >
                              Disconnect
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Placeholder for future integrations */}
      <div className="border border-dashed border-border rounded-lg p-6 text-center text-muted-foreground">
        <Settings className="size-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">More integrations coming soon.</p>
      </div>
    </div>
  );
}
